import { TFunction } from 'i18next';
import { MetricResult, SQLFilters } from '../../../../../api/types';
import {
  $UndefinedValueIndicator$,
  UNDEFINED_DISPLAY_KEY,
} from '../../../../../constants';
import {
  DataFieldWithDataType,
  GroupBy,
  MetricIdType,
  SingleValueTimeSelection,
} from '../../../../../types';
import { getDisplayValueForMaybeUndefinedValue } from '../../../../../utils';
import { DataFieldValueFormatter } from '../../../../hooks';
import { KpiBenchmarkConfig, MetricQueryConfig } from '../../common/types';
import {
  getDefaultKpiBenchmarkTitle,
  getFormattedKpiBenchmarkValue,
  getMainAndOptionalBenchmarkQueries,
} from '../../common/utils';
import { TimePeriodPieBenchmarkSubRow } from '../types';

const isMissingGroupSegmentKey = (key: string): boolean => {
  const rawValues = JSON.parse(key) as (string | null)[];
  return rawValues.some(
    (value) =>
      value === null ||
      (typeof value === 'string' && value.includes($UndefinedValueIndicator$))
  );
};

const groupValueToKey = (groupValue: (string | null)[]): string =>
  JSON.stringify(groupValue);

const keyToGroupValue = (key: string): (string | null)[] =>
  JSON.parse(key) as (string | null)[];

const extractGroupTotalsByKeyFromMetricResult = (
  metricResult: MetricResult,
  omitZero: boolean
): Map<string, number> => {
  const map = new Map<string, number>();
  const groupSegments = metricResult.segments[0]?.groupSegments ?? [];
  for (const groupSegment of groupSegments) {
    const total = Number(groupSegment.data) || 0;
    if (omitZero && total === 0) {
      continue;
    }
    const groupValue = groupSegment.groupSegment.map(
      (segment) => segment.value as string | null
    );
    map.set(groupValueToKey(groupValue), total);
  }
  return map;
};

export const buildTimePeriodPieBenchmarkSubRows = (params: {
  mainMetricResult: MetricResult;
  benchmarkMetricResults: MetricResult[];
  benchmarkConfigs: KpiBenchmarkConfig[];
  groupByDataField: DataFieldWithDataType;
  dataFieldValueFormatter: DataFieldValueFormatter;
  valueFormatter: (value: number) => string;
  t: TFunction;
}): TimePeriodPieBenchmarkSubRow[] => {
  const {
    mainMetricResult,
    benchmarkMetricResults,
    benchmarkConfigs,
    groupByDataField,
    dataFieldValueFormatter,
    valueFormatter,
    t,
  } = params;

  const mainByKey = extractGroupTotalsByKeyFromMetricResult(
    mainMetricResult,
    true
  );
  const benchmarkByKeyMaps = benchmarkMetricResults.map((metricResult) =>
    extractGroupTotalsByKeyFromMetricResult(metricResult, false)
  );
  const sortedKeys = [...mainByKey.keys()].sort((keyA, keyB) => {
    const isMissingA = isMissingGroupSegmentKey(keyA);
    const isMissingB = isMissingGroupSegmentKey(keyB);
    if (isMissingA !== isMissingB) {
      return isMissingA ? 1 : -1;
    }
    return 0;
  });

  const rows: TimePeriodPieBenchmarkSubRow[] = [];
  for (const key of sortedKeys) {
    const mainTotal = mainByKey.get(key);
    if (mainTotal === undefined) {
      continue;
    }

    const sliceLabel = getDisplayValueForMaybeUndefinedValue(
      dataFieldValueFormatter(keyToGroupValue(key), groupByDataField)
    );

    benchmarkConfigs.forEach((benchmarkConfig, benchmarkIndex) => {
      const benchmarkByKey = benchmarkByKeyMaps[benchmarkIndex];
      if (!benchmarkByKey) {
        return;
      }

      const benchmarkTotal = benchmarkByKey.get(key);
      const benchmarkQueryValue =
        benchmarkTotal === undefined ? null : benchmarkTotal;
      const benchmarkTitle = getDefaultKpiBenchmarkTitle(
        benchmarkConfig.benchmarkPeriod,
        benchmarkConfig.benchmarkType,
        t
      );
      const nullIndicator = t(UNDEFINED_DISPLAY_KEY);
      const value = getFormattedKpiBenchmarkValue(
        benchmarkConfig.benchmarkType,
        benchmarkQueryValue,
        mainTotal,
        valueFormatter,
        nullIndicator
      );
      if (value === nullIndicator) {
        return;
      }
      rows.push({
        title: `${benchmarkTitle} — ${sliceLabel}`,
        value,
      });
    });
  }

  return rows;
};

export const getMetricQueriesForTimePeriodUnsegmentedWithOptionalPieBenchmarks =
  (params: {
    queries: { metric: MetricIdType }[];
    timeSelection: SingleValueTimeSelection;
    groupBy: GroupBy;
    filters?: SQLFilters;
    benchmarkConfigs?: KpiBenchmarkConfig[];
    includePieBenchmarks: boolean;
  }): MetricQueryConfig[] => {
    const {
      queries,
      timeSelection,
      groupBy,
      filters,
      benchmarkConfigs,
      includePieBenchmarks,
    } = params;

    const mainQueries = queries.map((query) => ({
      metric: query.metric,
      timeSelection,
      filters,
      segmentations: [groupBy],
    }));

    const includeBenchmarks = includePieBenchmarks && queries.length === 1;
    const benchmarkBaseQuery =
      queries.length === 1
        ? {
            metric: queries[0].metric,
            timeSelection,
            filters,
            segmentations: [groupBy],
          }
        : undefined;

    return getMainAndOptionalBenchmarkQueries({
      mainQueries,
      benchmarkConfigs,
      includeBenchmarks,
      benchmarkBaseQuery,
    });
  };
